import type { OpportunityOnboarding, Taxonomy } from '@axiom/validation';
import { CountryCodesConst, CandidatesConst } from '@axiom/const';

import { PluralizeUtil } from '../utils/pluralize-util';
import { TaxonomyUtil } from '../utils/taxonomy-util';

const { CountryCodes, CountryCodesAbbreviations: CountryCodeAbbr } =
  CountryCodesConst;
const { EmployeeTypes } = CandidatesConst;

type Position = NonNullable<OpportunityOnboarding['positions']>[number];

type OnboardingTexts = {
  setUpSuccess: string | null;
  startedWithAxiom: string | null;
  benefits: string | null;
  holiday: string | null;
  weeklyCommitment: string;
  overtime: string | null;
  resources: string | null;
  describingAxiom: string;
  supportResources: string | null;
  communications: string | null;
  workLocationWorkingRemotely: string | null;
  warnings: string[];
} | null;

export type UseOnboardingTextReturn = {
  getOnboardingTexts: (pos?: Position) => OnboardingTexts;
  formatEngagementAddress: (withJobName?: boolean) => string[];
};

export type OnboardingUrls = {
  onboardingTimeEntryUrl: string | null;
  onboardingLinkedinGuideUrl: string | null;
  onboardingAodaInstructionsUrl: string | null;
  onboardingLsoLawproUrl: string | null;
  onboardingLawSocietyGuideUrl: string | null;
  onboardingEngagementSuccessUrl: string | null;
  onboardingEncryptedEmailUrl: string | null;
  workdayHelpcenterUrl: string | null;
};

const getCountryCode = (pos: Position) => {
  return (
    pos.candidateOpportunities?.[0]?.candidate.addressCountryCode ??
    CountryCodeAbbr.US
  );
};

const getIsContractor = (pos: Position) => {
  return (
    pos.candidateOpportunities?.[0]?.candidate.employeeType ===
    CandidatesConst.EmployeeTypes.Contractor
  );
};

export const useOnboardingText = ({
  data,
  urls,
  taxonomy,
}: {
  data: OpportunityOnboarding;
  urls: OnboardingUrls;
  taxonomy: Taxonomy;
}) => {
  const warningsSet = new Set<string>();

  const getFormattedEngagementAddress = (withJobName = true): string[] => {
    const textBits = [];

    if (withJobName && data.jobName) {
      textBits.push(data.jobName);
    }
    if (data.addressStreet) {
      textBits.push(data.addressStreet);
    }
    const cityStateParts = [];
    if (data.addressCity) {
      cityStateParts.push(data.addressCity);
    }
    if (data.addressState) {
      cityStateParts.push(data.addressState);
    }
    const locationLineParts = [];
    if (cityStateParts.length > 0) {
      locationLineParts.push(cityStateParts.join(', '));
    }
    if (data.addressZip) {
      locationLineParts.push(data.addressZip);
    }
    if (locationLineParts.length > 0) {
      textBits.push(locationLineParts.join(' '));
    }

    return textBits;
  };

  const getSetUpSuccessText = (pos: Position) => {
    if (!pos.candidateOpportunities?.[0]?.isFirstOnboarding) {
      return (
        `As you prepare to begin your new engagement, please ensure all required engagement documentation is completed in advance of your start date. Incomplete documentation may delay your ability to begin as scheduled.\n\n` +
        `You can find a full checklist and guidance on how to have a successful engagement [here](${urls.onboardingEngagementSuccessUrl}).\n\n` +
        `Complete any outstanding onboarding tasks (personal details, acknowledgements, required forms) in ${urls.workdayHelpcenterUrl ? `[Workday](${urls.workdayHelpcenterUrl})` : 'Workday'}. Reach out to the [Talent Onboarding Team](mailto:talent_onboarding@axiomlaw.com) if you have any questions.\n`
      );
    }

    return null;
  };

  const getStartedWithAxiomText = ({
    pos,
    cc,
  }: {
    pos: Position;
    cc: string;
  }) => {
    let text = null;

    if (pos.candidateOpportunities?.[0]?.isFirstOnboarding) {
      text =
        `Please complete any outstanding onboarding tasks in Workday as soon as possible. If onboarding is not completed on time, it may impact your ability to start the engagement as scheduled.\n` +
        `- You will receive an encrypted email with your IT credentials from your TSP.  If you need help opening this email please refer to this [guide](${urls.onboardingEncryptedEmailUrl}).\n` +
        `- Complete any outstanding onboarding tasks (personal details, acknowledgements, required forms) in ${urls.workdayHelpcenterUrl ? `[Workday](${urls.workdayHelpcenterUrl})` : 'Workday'}.  Reach out to the [Talent Onboarding Team](mailto:talent_onboarding@axiomlaw.com) if you have any questions.\n` +
        `- Ensure any outstanding employment and engagement documentation is completed.\n` +
        `- You can find a full checklist and guidance on how to have a successful engagement [here](${urls.onboardingEngagementSuccessUrl}).\n`;

      if (cc === CountryCodeAbbr.CA) {
        text +=
          `- Ensure you have completed the AODA training task in Workday and reviewed the Insurance Status Guide.\n` +
          `  - [AODA instructions](${urls.onboardingAodaInstructionsUrl})\n`;

        if (
          (
            pos.candidateOpportunities[0].candidate.addressState ?? ''
          ).toLowerCase() === 'ontario'
        ) {
          text +=
            `  - [LSO and LawPRO Status for Axiom Employees](${urls.onboardingLsoLawproUrl})\n` +
            `  - [Law Society and Insurance Status Guide](${urls.onboardingLawSocietyGuideUrl})\n`;
        }
      }
    }

    return text;
  };

  const getBenefitsText = (cc: string, isContractor: boolean) => {
    if (isContractor) {
      return 'You are not eligible for benefits.';
    }

    const hrHelpCenterText =
      'For information on our benefit offerings, eligibility criteria, and effective dates, view our HR Help Center and Benefits Guidebook in Workday.';
    const benefitsTextMap: Record<string, string> = {
      [CountryCodeAbbr.AU]:
        'You can find all your benefits information in your employment agreement.',
      [CountryCodeAbbr.US]: hrHelpCenterText,
      [CountryCodeAbbr.CA]: hrHelpCenterText,
      [CountryCodeAbbr.SG]: hrHelpCenterText,
      [CountryCodeAbbr.UK]: hrHelpCenterText,
    };

    return benefitsTextMap[cc] ?? null;
  };
  const getHolidayText = (cc: string, isContractor: boolean) => {
    if (isContractor) {
      return 'You are not eligible for holidays.';
    }

    const holidayTextMap: Record<string, string> = {
      [CountryCodeAbbr.US]:
        'You are eligible for Axiom recognized holidays, as outlined in the Employee Handbook, if you are working 40+ hours/week at the time the holiday arises. You should record those days on your timesheet as non-billable, against the client matter, with "holiday" in the comment section. Axiom will correct any inaccuracies for Axiom recognized holidays, if you have entered but you are otherwise not eligible. A list of your public holidays can be found in Workday > Resources Hub > Time off, Leave & Holidays.',
      [CountryCodeAbbr.UK]:
        'Public holidays accrue alongside your PTO and will be entered on your behalf, while on engagement. Please leave these dates blank on your timesheet.  A list of your public holidays can be found in Workday > Resources Hub > Time off, Leave & Holidays.',
      [CountryCodeAbbr.CA]:
        'You are eligible for Axiom recognized holidays, as outlined in the Employee Handbook, if you are working 32+ hours/week at the time the holiday arises. You should record those days on your timesheet as non-billable, against the client matter, with "holiday" in the comment section. Axiom will correct any inaccuracies, if you have entered but you are otherwise not eligible. A list of your holidays can be found in Workday Resources Hub > Time off, Leave & Holidays.',
      [CountryCodeAbbr.HK]:
        'You will be automatically granted public holidays, prorated for time worked as outlined in the Employee Handbook. Leave those dates blank on your timesheet. You can find a full list of holidays in Workday > Resources Hub > Time off, Leave & Holidays.',
      [CountryCodeAbbr.SG]:
        'You will be automatically granted public holidays, prorated for time worked as outlined in the Employee Handbook. Leave those dates blank on your timesheet. You can find a full list of holidays in Workday > Resources Hub > Time off, Leave & Holidays.',
      [CountryCodeAbbr.AU]:
        'Information on your public holidays can be found in your employment agreement.',
    };

    return holidayTextMap[cc] ?? null;
  };
  const getWeeklyCommitmentText = (pos: Position, isContractor: boolean) => {
    const hoursPerWeek = `${pos.billingHoursPerWeek ?? 0} ${PluralizeUtil({ text: 'hour', count: pos.billingHoursPerWeek ?? 0 })} per week`;

    if (pos.reservedType === 'Reserved') {
      return (
        `${data.account.calculatedName} has reserved ${hoursPerWeek} of your time. You should enter the full ${hoursPerWeek}.\n` +
        `- You must enter and submit your reserved time before the deadlines listed in your timesheet calendar. Please ensure to "Review" and "Submit" your time. You can find your timesheet calendar on Workday > Resources Hub > Time Off, Leave & Holidays > Timesheet Calendars. Please complete your timesheet in accordance with the Axiom Timesheet Compliance Policy to ensure you are paid the correct amount and clients are billed accurately.\n` +
        `- Please enter your time in Workday with a general description of the work completed (e.g. Commercial Contracts and Privacy Support to Company X) or as directed by your Talent Success Partner or Client Relationship Manager to comply with any specific client instructions. Both Axiom and the client will be reviewing the invoice and your narrative. Please do not include any confidential or privileged information.\n` +
        `- If you complete fewer hours than the reserved amount, you should enter/identify the balance of hours in such a given week (not corresponding to work completed) with the following narrative: "Time entered in accordance with Axiom's Reserved Engagement".\n${
          isContractor
            ? ''
            : '- You must also enter approved PTO in your timesheet in hours.\n'
        }- If you're unable to work due to illness, notify your TSP and client ASAP on day 1 (with expected return date) and record sickness via Axiom Answers or your TSP - do not charge absence to the client matter.\n${
          isContractor
            ? ''
            : `- You can find step by step instructions on entering time and absences, [here](${urls.onboardingTimeEntryUrl}).\n`
        }`
      );
    }

    return (
      `${data.account.calculatedName} has allocated approximately ${hoursPerWeek} of your time on a variable basis. Please record the hours you actually work and provide a brief summary.  Do not include confidential information or detail about your matter.  Do provide helpful summaries that give detail of the work done.\n` +
      `- You must also enter approved PTO in your timesheet in hours.\n` +
      `- If you're unable to work due to illness, notify your TSP and client ASAP on day 1 (with expected return date) and record sickness via Axiom Answers or your TSP - do not charge absence to the client matter.\n` +
      `- You can find step by step instructions on entering time and absences, [here](${urls.onboardingTimeEntryUrl}).\n`
    );
  };

  const getOvertimeText = (pos: Position) => {
    return pos.reservedType === 'Reserved'
      ? `Before working any time beyond your weekly reserved hours, you must either:\n` +
          `- Receive confirmation from your Talent Success Partner that the client has agreed to additional hours; or\n` +
          `- Obtain written approval directly from the client and share this in writing with your Talent Success Partner in advance.\n\n` +
          `For questions about overtime, please contact ${pos.candidateOpportunities?.[0]?.candidate.ownerUser?.fullName ?? 'your Talent Success Partner'} and refer to the Time Recording and Timesheet Submission policy in your Workday Resources Hub > Corporate Policy Documents.`
      : null;
  };

  const getResourcesText = (pos: Position) => {
    return pos.candidateOpportunities?.[0]?.candidate.employeeType ===
      EmployeeTypes.Contractor
      ? null
      : `You'll have access to Axiom-supported legal resources to help you deliver work for your client. This includes tools like Practical Law Global and PLI for research and CLE, as well as Axiom hosted monthly CLEs and webinars. Access to certain tools is provided based on engagement needs - just reach out to [answers@axiomlaw.com](mailto:answers@axiomlaw.com) with any questions.`;
  };
  const getDescribingAxiomText = () => {
    return `We know that you may be interested in updating your LinkedIn Profile with this new engagement. As a reminder you cannot list the client as your employer – please review these [Linkedin Guidelines](${urls.onboardingLinkedinGuideUrl}) for more information.`;
  };
  const getSupportResourcesText = (isContractor: boolean) => {
    return isContractor
      ? null
      : `Axiom for Talent includes a range of support and resources to help you stay informed and get what you need quickly. In the **AFT Help Hub**, you'll find the **Message Center** for important updates and announcements, the **Knowledge Center** for on-demand guidance and resources, and the **Support Center** for details on key Axiom contacts. You can also access the **Workday Help Center** for assistance with Workday-related tasks. Keep your **Axiom for Talent profile** up to date so you receive the right information and support throughout your engagement.`;
  };

  const getCommunicationsText = ({
    pos,
    cc,
  }: {
    pos: Position;
    cc: string;
  }) => {
    const businessTitle =
      TaxonomyUtil.getBusinessTitle(
        taxonomy,
        pos.candidateOpportunities?.[0]?.candidate.businessTitle ?? null
      )?.displayName ?? null;
    const barredLocationsMap = new Map(
      pos.candidateOpportunities?.[0]?.candidate.barredLocations.map(bl => [
        bl.name,
        bl.location,
      ]) ?? null
    );
    const isLawyer = TaxonomyUtil.isLawyer(
      pos.candidateOpportunities?.[0]?.candidate.businessTitle ?? null
    );

    const baseText =
      'This must be used in any communications or materials which reference you including your business card, email signature and Axiom for Talent profile.\n';

    const getBarristerSolicitor = () => {
      const settings = pos.candidateOpportunities?.[0]?.candidate.settings;
      const texts = [];

      const found = settings?.lawyerSpecificType.find(({ countryCode }) => {
        return countryCode === cc;
      });

      if (found) {
        if (barredLocationsMap.has('UK - England & Wales')) {
          texts.push(
            ...found.type.map(type => {
              return type === 'Barrister'
                ? 'Lawyer who previously qualified as a Barrister in England & Wales'
                : 'Solicitor qualified in England & Wales';
            })
          );
        }

        if (barredLocationsMap.has('UK - Northern Ireland')) {
          texts.push(
            ...found.type.map(type => {
              return type === 'Barrister'
                ? 'Lawyer who previously qualified as a Barrister in Northern Ireland'
                : 'Solicitor qualified in Northern Ireland';
            })
          );
        }
      }

      return texts.join('\n');
    };

    const getUkText = () => {
      let secondBit: string = businessTitle || '';

      if (isLawyer) {
        if (
          barredLocationsMap.has('UK - England & Wales') ||
          barredLocationsMap.has('UK - Northern Ireland')
        ) {
          const barristerSolicitorText = getBarristerSolicitor();

          if (barristerSolicitorText) {
            secondBit = barristerSolicitorText;
          }
        } else if (barredLocationsMap.has('EU - Registered EU Lawyer')) {
          secondBit = 'Registered European Lawyer';

          if (barredLocationsMap.size <= 1) {
            warningsSet.add(
              'This lawyer appears to be missing an admission. This talent has an admission of "EU - Registered EU Lawyer" and is expected to also have an admission in Switzerland by Axiom’s business rules.'
            );
          }
        } else if (barredLocationsMap.has('UK - Registered Foreign Lawyer')) {
          secondBit = `Registered Foreign Lawyer qualified in ${[...barredLocationsMap.values()].join(', ')}`;

          if (barredLocationsMap.size <= 1) {
            warningsSet.add(
              'This lawyer appears to be missing an admission. This talent has an admission of "UK - Registered Foreign Lawyer" and is expected to also have an additional admission, but an additional admission was not found.'
            );
          }
        } else if (barredLocationsMap.size > 0) {
          secondBit = `Non-practising lawyer qualified in ${[...barredLocationsMap.values()].join(', ')}`;
        } else {
          secondBit = `A bar admission is missing`;
          warningsSet.add(
            'This lawyer appears to be missing an admission. They are set to ‘Lawyer’ but do not have an admission.'
          );
        }
      }

      return `${baseText}**${secondBit}**\n`;
    };

    const qualificationMap: Record<string, string> = {
      [CountryCodeAbbr.UK]: getUkText(),
      [CountryCodeAbbr.SG]: `${baseText}**Legal Consultant qualified in ${CountryCodes[CountryCodeAbbr.SG]}**\n`,
      [CountryCodeAbbr.HK]: `${baseText}**Legal Consultant qualified in ${CountryCodes[CountryCodeAbbr.HK]}**\n`,
    };

    return qualificationMap[cc] ?? null;
  };

  const getWorkLocationWorkingRemotelyText = (cc: string) => {
    if (cc === CountryCodeAbbr.SG || cc === CountryCodeAbbr.HK) {
      const countryName = CountryCodes[cc];
      return (
        `As per your employment agreement, please ensure there is no reference to a current Hong Kong / Singapore practicing certificate, that there is no reference to being a currently qualified solicitor or barrister in Hong Kong / Singapore and that the description of services you provide to Axiom and its clients are those of business consultants, not legal services.\n\n` +
        `In line with the above, please also ensure your title is 'Consultant' in any communications or materials which reference you, including your business card, email signature (Axiom and Client email) and Axiom for Talent profile.\n\n` +
        `Remember: You are required to work in ${countryName} during your engagements. If you plan to work outside ${countryName}, including on holiday, this must be approved by Axiom and the client in advance in accordance with the Axiom Legal Talent Handbook in Workday > Resources Hub > Corporate Policy Documents for more information.\n`
      );
    }

    return cc === CountryCodeAbbr.UK
      ? `Remember: You are required to physically work in the UK during Axiom engagements. If you plan to work outside the UK, including on holiday, this must be approved by Axiom and the client in advance. Please review the relevant section of our Legal Talent Handbook in Workday > Resources Hub > Corporate Policy Documents for more information.\n`
      : null;
  };

  return {
    getOnboardingTexts: (pos?: Position): OnboardingTexts => {
      if (!pos) return null;

      const cc = getCountryCode(pos);
      const isContractor = getIsContractor(pos);

      return {
        setUpSuccess: getSetUpSuccessText(pos),
        startedWithAxiom: getStartedWithAxiomText({ pos, cc }),
        benefits: getBenefitsText(cc, isContractor),
        holiday: getHolidayText(cc, isContractor),
        weeklyCommitment: getWeeklyCommitmentText(pos, isContractor),
        resources: getResourcesText(pos),
        overtime: getOvertimeText(pos),
        describingAxiom: getDescribingAxiomText(),
        supportResources: getSupportResourcesText(isContractor),
        communications: getCommunicationsText({ pos, cc }),
        workLocationWorkingRemotely: getWorkLocationWorkingRemotelyText(cc),
        warnings: [...warningsSet],
      };
    },
    formatEngagementAddress: getFormattedEngagementAddress,
  };
};
